import React from 'react';
import {createPortal} from 'react-dom';
import {ErrorBoundary} from './ErrorBoundary';
import {
  HtmlFragment,
  ExternalCustomCode,
  CurrentPageCustomCode,
  telemetrySendError,
} from '@packages/systems/designer/canvas';
import {CodeBlock} from '@packages/systems/customCode/ui';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {DynamoData} from '@packages/systems/dynamo/graphql';
import {ApolloClientStateType} from '@packages/systems/designer/apollo-client';
import {CurrencySettings} from '@packages/systems/core/utils/CurrencyUtils';
import {BetaFeatureStateType} from '@packages/systems/designer/beta-features';
import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';

type BodyEndCustomCodeProps = {
  customCodeSitePostBody: string | undefined;
  currentPage: PageRecordType;
  timezone: string | null | undefined;
  collections: CollectionsType | undefined;
  betaFeatureState: BetaFeatureStateType | undefined;
  isReadyToBeInserted: boolean;
  dynamoData: DynamoData | null;
  externalCustomCode: CodeBlock[];
  pageId: string;
  ecommCurrencySettings: CurrencySettings | undefined;
  apolloClient: ApolloClientStateType;
};

/**
 * Inserts custom code that should be inserted before the closing `</body>` tag.
 */
export const BodyEndCustomCode = ({
  customCodeSitePostBody,
  currentPage,
  timezone,
  ecommCurrencySettings,
  collections,
  betaFeatureState,
  apolloClient,
  isReadyToBeInserted,
  dynamoData,
  externalCustomCode,
  pageId,
}: BodyEndCustomCodeProps) => {
  // Get the body element of the document
  const bodyElement = document.body;

  if (!bodyElement) return null;

  return createPortal(
    <ErrorBoundary
      onError={(error) => {
        console.error('Custom code error:', error);
        telemetrySendError({
          name: 'Custom code postBody error',
          error,
        });
      }}
    >
      <HtmlFragment
        fragment={customCodeSitePostBody}
        isReadyToBeInserted={isReadyToBeInserted}
      />
      <CurrentPageCustomCode
        fragment={currentPage.postBody}
        currentPage={currentPage}
        timezone={timezone}
        currencySettings={ecommCurrencySettings}
        collections={collections}
        betaFeatureState={betaFeatureState}
        apolloClient={apolloClient}
        isReadyToBeInserted={isReadyToBeInserted}
        dynamoData={dynamoData}
      />
      <ExternalCustomCode
        externalCustomCode={externalCustomCode}
        position="footer"
        pageId={pageId}
        isReadyToBeInserted={isReadyToBeInserted}
      />
    </ErrorBoundary>,
    bodyElement
  );
};
